/**
 * Auto-apply discount codes for featured-product sections only.
 */
(function () {
  const sections = new Map();
  let pendingSectionId = null;
  let pendingDiscountCodes = null;
  let discountAppliedForSection = null;
  let hooksInstalled = false;
  let submitListenerInstalled = false;

  function normalizeCodes(codes) {
    if (!Array.isArray(codes)) return [];

    const seen = new Set();
    const result = [];

    codes.forEach((value) => {
      const code = (value || "").trim();
      if (!code || seen.has(code)) return;
      seen.add(code);
      result.push(code);
    });

    return result;
  }

  function codesKey(codes) {
    return normalizeCodes(codes).sort().join("\0");
  }

  function clearPending() {
    pendingSectionId = null;
    pendingDiscountCodes = null;
  }

  function parseCodesFromDataset(value) {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return normalizeCodes(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      return [];
    }
  }

  function isActiveCodes(sectionId, codes) {
    if (!sectionId) return false;

    const registered = sections.get(sectionId);
    if (!registered?.length) return false;

    return codesKey(registered) === codesKey(codes);
  }

  async function clearCartDiscount() {
    const root = window.Shopify?.routes?.root ?? "/";

    try {
      await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ discount: "" }),
      });
    } catch (e) {
      /* ignore */
    }
  }

  async function applyDiscounts(codes) {
    const list = normalizeCodes(codes);
    if (!list.length) return;

    const root = window.Shopify?.routes?.root ?? "/";
    await clearCartDiscount();

    for (const code of list) {
      await fetch(`${root}discount/${encodeURIComponent(code)}`, {
        credentials: "same-origin",
      });
    }
  }

  async function fetchCartSections(cart) {
    if (!cart?.getSectionsToRender) return null;

    const root = window.Shopify?.routes?.root ?? "/";
    const sectionIds = cart
      .getSectionsToRender()
      .map((section) => section.id)
      .join(",");

    const response = await fetch(`${root}?sections=${sectionIds}`);
    if (!response.ok) return null;

    return { sections: await response.json() };
  }

  function installSubmitListener() {
    if (submitListenerInstalled) return;
    submitListenerInstalled = true;

    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target.closest?.('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const sectionRoot = form.closest("[data-oo-fp-discount-root]");
        if (!sectionRoot) return;

        const sectionId = sectionRoot.dataset.ooFpDiscountSectionId;
        const codes = parseCodesFromDataset(
          form.closest("product-form")?.dataset.ooAutoDiscountCodes,
        );

        clearPending();

        if (!isActiveCodes(sectionId, codes)) return;

        pendingSectionId = sectionId;
        pendingDiscountCodes = codes;
      },
      true,
    );
  }

  function installCartUpdateHook() {
    if (window.__ooFpDiscountCartHook) return;
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
      return;
    }

    window.__ooFpDiscountCartHook = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== "product-form") return;

      const codes = pendingDiscountCodes;
      const sectionId = pendingSectionId;
      clearPending();

      if (!isActiveCodes(sectionId, codes)) return;

      discountAppliedForSection = sectionId;

      (async () => {
        try {
          await applyDiscounts(codes);
          const cart = document.querySelector("cart-drawer");
          if (typeof cart?.refreshCartDrawer === "function") {
            cart.refreshCartDrawer({});
          }
        } catch (e) {
          console.warn("[oo-featured-product-discount]", e);
          discountAppliedForSection = null;
        }
      })();
    });
  }

  function installRenderHook() {
    const cart = document.querySelector("cart-drawer");
    if (!cart || cart.__ooFpDiscountRenderHook) return;

    cart.__ooFpDiscountRenderHook = true;
    const originalRender = cart.renderContents.bind(cart);

    cart.renderContents = function (parsedState, isClosedCart = true) {
      const sectionId = discountAppliedForSection;
      const codes = sectionId ? sections.get(sectionId) : null;

      discountAppliedForSection = null;

      if (!sectionId || !codes?.length) {
        return originalRender(parsedState, isClosedCart);
      }

      return (async () => {
        try {
          const refreshed = await fetchCartSections(cart);
          originalRender(refreshed || parsedState, isClosedCart);
        } catch (e) {
          console.warn("[oo-featured-product-discount]", e);
          originalRender(parsedState, isClosedCart);
        }
      })();
    };
  }

  function ensureHooks() {
    if (hooksInstalled) return;
    hooksInstalled = true;
    installSubmitListener();
    installCartUpdateHook();
    installRenderHook();
  }

  function unregister(sectionId) {
    sections.delete(sectionId);
    clearPending();

    if (discountAppliedForSection === sectionId) {
      discountAppliedForSection = null;
    }

    clearCartDiscount();
  }

  window.__ooRegisterFeaturedProductDiscount = function ({
    sectionId,
    discountCode,
    discountCodes,
  }) {
    if (!sectionId) return;

    let codes = discountCodes;

    if (!Array.isArray(codes)) {
      codes = discountCode ? [discountCode] : [];
    }

    codes = normalizeCodes(codes);

    if (!codes.length) {
      unregister(sectionId);
      return;
    }

    sections.set(sectionId, codes);
    ensureHooks();
  };

  document.dispatchEvent(new CustomEvent("oo-fp-discount-ready"));
})();
